import React from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import { useIntl } from 'react-intl'
import { FuseAnimateGroup } from '@fuse'
import { makeStyles } from '@material-ui/styles'
import {
  Divider as BaseDivider,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'

import { isEmptyOrNil } from 'libs/empty'
import Loader from 'app/components/Loader'
import appConfigs from 'app/configs/appConfigs'
import { hasPlanCanceled } from 'commons/useSubscription'
import {
  isFreeTierOrFreeTrialPlanByPlanId,
  isPhysicalWebStarterPackByPlanId,
} from 'commons/plan'

import { BRAINTREE_CASH } from '../constants'
import { CurrentUsage } from './CurrentUsage'
import { AccumulatedUsage } from './AccumulatedUsage'
import { PlanBillingDetails } from './PlanBillingDetails'
import { PlanBillingYourPlan } from './PlanBillingYourPlan'
import { PlanBillingPaymentMethod } from './PlanBillingPaymentMethod'

import _ from '@lodash'

const Divider = (props) => <BaseDivider className="my-24" {...props} />

const useStyles = makeStyles((theme) => ({
  backgroundPlan: {
    background: 'linear-gradient(180deg, #00B2AD 0%, #0372B1 100%)',
  },
  textBalance: {
    background: 'linear-gradient(180deg, #00B2AD 0%, #0372B1 100%)',
    '-webkit-text-fill-color': 'transparent',
    '-webkit-background-clip': 'text',
  },
}))

export function PlanBillingInfoPanel({
  isOpenChangePlanDialog,
  planBill,
  payment,
  accumulate,
  isLoading,
  onFetchPlanAndBilling,
  onFetchPaymentAddress,
}) {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const braintreeId = R.path(['activeSubscription', 'braintreeId'], planBill)
  const isPlanAddManual = R.equals(braintreeId, BRAINTREE_CASH)
  const tierOrTrial = isFreeTierOrFreeTrialPlanByPlanId(
    R.path(['activeSubscription', 'plan', 'id'], planBill),
  )
  const isCancelPlan =
    !tierOrTrial &&
    R.not(isPlanAddManual) &&
    appConfigs.enableChangePlan &&
    !hasPlanCanceled(R.prop('activeSubscription', planBill)) &&
    !R.path(['activeSubscription', 'overdraftCount'], planBill) &&
    !R.prop('nextSubscription', planBill)

  return (
    <>
      <div className="md:flex full space-y-5 md:space-y-0">
        <div
          className={clsx(
            classes.backgroundPlan,
            'p-10 rounded-8 full md:w-4/6 text-white md:mr-5',
          )}
        >
          <PlanBillingYourPlan
            planBill={planBill}
            payment={payment}
            isLoading={isLoading}
            shippingAddress={R.prop('shippingAddress', payment)}
            isOpenChangePlanDialog={isOpenChangePlanDialog}
            onFetch={() => {
              const debounce = _.debounce(() => onFetchPlanAndBilling(), 2000)
              debounce()
            }}
          />
        </div>
        <div className="full md:w-2/6 md:ml-5 flex justify-center md:justify-end items-center">
          <div className="">
            <div className="flex justify-center md:justify-end">
              {formatMessage({
                id: 'planAndBilling.accumulatedUsage.label',
              })}
            </div>
            <Tooltip
              title={formatMessage({
                id: 'planAndBilling.accumulatedUsage.tooltip',
              })}
              placement="bottom"
            >
              <div
                className={clsx(
                  classes.textBalance,
                  'flex justify-center md:justify-end text-48 font-bold cursor-default h-67',
                )}
              >
                <Loader
                  isLoading={isLoading}
                  placeholder={
                    <FuseAnimateGroup
                      enter={{ animation: 'fadeIn' }}
                      leave={{ animation: 'fadeOut' }}
                    >
                      <div className="flex h-44 items-center">
                        <CircularProgress size={20} />
                      </div>
                    </FuseAnimateGroup>
                  }
                >
                  {(
                    R.path(['accumulate', 'usage'], planBill) ?? 0
                  ).toLocaleString('en-US')}
                </Loader>
              </div>
            </Tooltip>

            <div className="flex justify-center md:justify-end text-grey-66">
              {formatMessage({
                id: 'planAndBilling.credits.label',
              })}
            </div>

            <div className="flex justify-center md:justify-end">
              <Divider className="w-full my-5" />
            </div>

            <div className="flex justify-center md:justify-end">
              {formatMessage({
                id: 'planAndBilling.accumulatedBalance.label',
              })}
            </div>
            <Tooltip
              title={formatMessage({
                id: 'planAndBilling.accumulatedBalance.tooltip',
              })}
              placement="bottom"
            >
              <div
                className={clsx(
                  classes.textBalance,
                  'flex justify-center md:justify-end text-48 font-bold cursor-default h-67',
                )}
              >
                <Loader
                  isLoading={isLoading}
                  placeholder={
                    <FuseAnimateGroup
                      enter={{ animation: 'fadeIn' }}
                      leave={{ animation: 'fadeOut' }}
                    >
                      <div className="flex h-44 items-center">
                        <CircularProgress size={20} />
                      </div>
                    </FuseAnimateGroup>
                  }
                >
                  {(
                    R.path(['accumulate', 'balance'], planBill) ?? 0
                  ).toLocaleString('en-US')}
                </Loader>
              </div>
            </Tooltip>

            <div className="flex justify-center md:justify-end text-grey-66">
              {formatMessage({
                id: 'planAndBilling.credits.label',
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:flex md:justify-between space-y-5 md:space-y-0">
        <div className="w-full md:w-1/2 md:mr-5 ">
          <CurrentUsage planBill={planBill} />
        </div>
        <div className="w-full md:w-1/2 md:ml-5 ">
          <AccumulatedUsage
            accumulate={accumulate}
            beacon={R.path(['balance', 'totalBeacon'], planBill)}
          />
        </div>
      </div>
      <div className="md:flex full space-y-5 md:space-y-0">
        {!isEmptyOrNil(R.prop('paymentMethods', payment)) && (
          <div className="full md:w-4/6 bg-white md:mr-5 rounded-8 border-1 p-10">
            <PlanBillingPaymentMethod
              paymentMethod={R.prop('paymentMethods', payment)}
              isCancelPlan={isCancelPlan}
            />
          </div>
        )}
        {!isEmptyOrNil(R.prop('addresses', payment)) && (
          <div className="full md:w-2/6 bg-white md:ml-5 rounded-8 border-1 p-10">
            <PlanBillingDetails
              isPhysicalWebStarterPack={isPhysicalWebStarterPackByPlanId(
                R.path(['activeSubscription', 'plan', 'id'], planBill),
              )}
              billingAddress={R.prop('addresses', payment)}
              shippingAddress={R.prop('shippingAddress', payment)}
              handleSubmit={() => onFetchPaymentAddress()}
            />
          </div>
        )}
      </div>
    </>
  )
}
